import * as Yup from 'yup';
import { isValidDate } from '../helpers';

export const isRequired = (
  step: string,
  field: string
): Yup.StringSchema<string, unknown> => {
  return Yup.string()
    .nullable()
    .when('step', {
      is: (value) => value === step,
      then: Yup.string().required(`${field} is required`),
    });
};

export const isName = (
  step: string,
  field: string
): Yup.StringSchema<string, unknown> => {
  return Yup.string().when('step', {
    is: (value) => value === step,
    then: Yup.string()
      .required(`${field} is required`)
      .matches(/^[a-z '-]+$/i, {
        message: `${field} must not contain special characters`,
      }),
  });
};

export const noSpecialChars = (
  step: string,
  field: string
): Yup.StringSchema<string, unknown> => {
  return Yup.string().when('step', {
    is: (value) => value === step,
    then: Yup.string().matches(/^[a-z '-]+$/i, {
      message: `${field} must not contain special characters`,
      excludeEmptyString: true,
    }),
  });
};

export const personalDetails = {
  title: isRequired('2', 'Title'),
  gender: Yup.string()
    .when(['step', 'title'], {
      is: (step, title) => step === '2' && (title === 'mr' || title === 'rev'),
      then: Yup.string()
        .required(`Gender is required`)
        .test(
          'is-male',
          `Gender must match the title`,
          (gender) => gender === 'male'
        ),
    })
    .when(['step', 'title'], {
      is: (step, value) =>
        step === '2' && value !== 'mr' && value !== 'rev' && value !== 'dr',
      then: Yup.string().test(
        'is-female',
        `Gender must match the title`,
        (gender) => gender === 'female'
      ),
    }),
  forename: isName('2', 'First Name'),
  surname: isName('2', 'Last Name'),
  middlenames: noSpecialChars('2', 'Middlenames'),
  mother_maiden_name: isName('2', "Mother's Maiden Name"),
  birth_surname: isName('2', 'Birth Last Name'),
  date_of_birth_day: isRequired('2', 'Day').when('step', {
    is: (step: string) => step === '2',
    then: Yup.string().test(
      'date_of_birth',
      'Please enter a valid date',
      function (day) {
        const month = this.parent.date_of_birth_month;
        const year = this.parent.date_of_birth_year;
        return isValidDate(`${year}-${month}-${day}`, 'YYYY-MM-DD');
      }
    ),
  }),
  date_of_birth_month: isRequired('2', 'Month').when('step', {
    is: (step: string) => step === '2',
    then: Yup.string().test(
      'date_of_birth',
      'Please enter a valid date',
      function (month) {
        const day = this.parent.date_of_birth_day;
        const year = this.parent.date_of_birth_year;
        return isValidDate(`${year}-${month}-${day}`, 'YYYY-MM-DD');
      }
    ),
  }),
  date_of_birth_year: isRequired('2', 'Year').when('step', {
    is: (step: string) => step === '2',
    then: Yup.string().test(
      'date_of_birth',
      'Please enter a valid date',
      function (year) {
        const day = this.parent.date_of_birth_day;
        const month = this.parent.date_of_birth_month;
        return isValidDate(`${year}-${month}-${day}`, 'YYYY-MM-DD');
      }
    ),
  }),
  place_of_birth: isName('2', 'Place of Birth'),
  country_of_birth: isRequired('2', 'Country of Birth'),
};

export const addressDetails = {
  same_as_current_address: isRequired('3', 'Address question'),
  current_street_name: Yup.string().when(['step', 'same_as_current_address'], {
    is: (step, same_as_current_address) =>
      step === '3' && same_as_current_address === 'same_as_current_address_no',
    then: Yup.string()
      .required('Street Name is required')
      .matches(
        /^[a-z0-9\s\-./\\]+$/i,
        'Street Name must not contain special characters'
      ),
  }),
  current_town: Yup.string().when(['step', 'same_as_current_address'], {
    is: (step, same_as_current_address) =>
      step === '3' && same_as_current_address === 'same_as_current_address_no',
    then: Yup.string()
      .required('Town is required')
      .matches(
        /^[a-z0-9\s\-./\\]+$/i,
        'Town must not contain special characters'
      ),
  }),
  current_postcode: Yup.string()
    .when(['step', 'same_as_current_address'], {
      is: (step, same_as_current_address) =>
        step === '3' &&
        same_as_current_address === 'same_as_current_address_no',
      then: Yup.string()
        .required('Postcode is required')
        .matches(
          /^[a-z0-9\s\-./\\]+$/i,
          'Postcode must not contain special characters'
        ),
    })
    .max(10, 'Postcode must not exceed 10 characters'),
  current_country: Yup.string().when(['step', 'same_as_current_address'], {
    is: (value, same_as_current_address) =>
      value === '3' && same_as_current_address === 'same_as_current_address_no',
    then: Yup.string()
      .oneOf(
        ['GBR01', 'GBR02', 'GBR03'],
        'You must be a resident of England, Wales or Scotland'
      )
      .required(`Country is required`),
  }),
  current_years_lived: Yup.string().when('step', {
    is: (value) => value === '3',
    then: Yup.string()
      .required(`Years lived is required`)
      .matches(/^[0-9]{1,2}$/, {
        message: 'Years lived must be a number and less than your age',
      }),
  }),
  current_months_lived: Yup.string().when('step', {
    is: (value) => value === '3',
    then: Yup.string()
      .required(`Months lived is required`)
      .matches(/^[0-9]{1,2}$/, {
        message: 'Only numbers are allowed',
      })
      .test(
        'months-number',
        'Months lived must be between 0 and 12',
        (value) => {
          return Number(value) >= 0 && Number(value) <= 12;
        }
      ),
  }),
  licence_street_name: Yup.string()
    .nullable()
    .when('step', {
      is: (value) => value === '3',
      then: Yup.string()
        .required('Street Name is required')
        .matches(
          /^[a-z0-9\s\-./\\]+$/i,
          'Street Name must not contain special characters'
        ),
    }),
  licence_address_line_2: Yup.string().when('step', {
    is: (value) => value === '3',
    then: Yup.string().matches(/^[a-z0-9\s\-./\\]+$/i, {
      message: 'Address line 2 must not contain special characters',
      excludeEmptyString: true,
    }),
  }),
  licence_town: Yup.string()
    .nullable()
    .when('step', {
      is: (value) => value === '3',
      then: Yup.string()
        .required('Town is required')
        .matches(
          /^[a-z0-9\s\-./\\]+$/i,
          'Town must not contain special characters'
        ),
    }),
  licence_postcode: Yup.string()
    .nullable()
    .when('step', {
      is: (value) => value === '3',
      then: Yup.string()
        .required('Postcode is required')
        .matches(
          /^[a-z0-9\s\-./\\]+$/i,
          'Postcode must not contain special characters'
        )
        .max(10, 'Postcode must not exceed 10 characters'),
    }),
  licence_country: isRequired('3', 'Country'),
};

export const securityDetails = {
  passport_number: Yup.string().when('step', {
    is: (value) => value === '4',
    then: Yup.string()
      .matches(/^[0-9]{9}$/, {
        message: 'Passport Number must be 9 digits',
      })
      .required('Passport Number is required'),
  }),

  passport_expiry_date_day: isRequired('4', 'Day').when('step', {
    is: (step: string) => step === '4',
    then: Yup.string().test(
      'passport_expiry_date',
      'Please enter a valid date',
      function (day) {
        const month = this.parent.passport_expiry_date_month;
        const year = this.parent.passport_expiry_date_year;
        return isValidDate(`${year}-${month}-${day}`, 'YYYY-MM-DD');
      }
    ),
  }),
  passport_expiry_date_month: isRequired('4', 'Month').when('step', {
    is: (step: string) => step === '4',
    then: Yup.string().test(
      'passport_expiry_date',
      'Please enter a valid date',
      function (month) {
        const day = this.parent.passport_expiry_date_day;
        const year = this.parent.passport_expiry_date_year;
        return isValidDate(`${year}-${month}-${day}`, 'YYYY-MM-DD');
      }
    ),
  }),
  passport_expiry_date_year: isRequired('4', 'Year').when('step', {
    is: (step: string) => step === '4',
    then: Yup.string().test(
      'passport_expiry_date',
      'Please enter a valid date',
      function (year) {
        const day = this.parent.passport_expiry_date_day;
        const month = this.parent.passport_expiry_date_month;
        return isValidDate(`${year}-${month}-${day}`, 'YYYY-MM-DD');
      }
    ),
  }),

  ni_number: Yup.string().when('step', {
    is: (value) => value === '4',
    then: Yup.string().matches(
      /^[A-CEGHJ-PR-TW-Z]{1}[A-CEGHJ-NPR-TW-Z]{1}[0-9]{6}[A-D]{0,1}$/i,
      {
        message:
          'Please provide a valid National Insurance Number (eg. QQ123456C)',
      }
    ),
  }),
};

export const confirmation = {
  email: Yup.string().when(['step', '$customerId'], {
    is: (value, id) => value === '5' && !id,
    then: Yup.string()
      .matches(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        'Please enter a valid email address'
      )
      .required('Email is required'),
  }),
  confirm_email: Yup.string().when(['step', '$customerId'], {
    is: (value, id) => value === '5' && !id,
    then: Yup.string()
      .matches(
        /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/,
        'Please enter a valid email address'
      )
      .oneOf([Yup.ref('email'), null], 'Email confirmation must match email')
      .required('Confirmation email is required'),
  }),
  mobile_number: Yup.string().when(['step'], {
    is: (value) => value === '5',
    then: Yup.string()
      .test('no-leading-space', 'Mobile number cannot start with a space.', (value) => {
        return value ? !/^\s/.test(value) : true;
      })
      .matches(/^[0-9]{11}$/g, {
        message: 'Mobile phone must be a valid UK phone number (e.g: 07911123456).',
        excludeEmptyString: true,
      }),
  }),
  declaration: Yup.string().when('step', {
    is: (value) => value === '5',
    then: Yup.string().oneOf(
      ['declaration'],
      'Accepting the declaration is required'
    ),
  }),
};

export const disqualification = {
  disqualified: isRequired('1', 'Disqualification Question'),
  disqualification_date_day: Yup.string().when(['step', 'disqualified'], {
    is: (step, disqualified) =>
      step === '1' && disqualified === 'disqualified_yes',
    then: Yup.string().required(`Day is required`),
  }),
  disqualification_date_month: Yup.string().when(['step', 'disqualified'], {
    is: (step, disqualified) =>
      step === '1' && disqualified === 'disqualified_yes',
    then: Yup.string().required(`Month is required`),
  }),
  disqualification_date_year: Yup.string().when(['step', 'disqualified'], {
    is: (step, disqualified) =>
      step === '1' && disqualified === 'disqualified_yes',
    then: Yup.string().required(`Year is required`),
  }),
  disqualification_court: Yup.string().when(['step', 'disqualified'], {
    is: (step, disqualified) =>
      step === '1' && disqualified === 'disqualified_yes',
    then: Yup.string()
      .required(`Court Name is required`)
      .matches(
        /^[a-z0-9\s\-./\\]+$/i,
        'Court Name must not contain special characters'
      ),
  }),
  disqualification_country: Yup.string().when(['step', 'disqualified'], {
    is: (step, disqualified) =>
      step === '1' && disqualified === 'disqualified_yes',
    then: Yup.string().required(`Country is required`),
  }),
};

[...Array(5)].map((_, index) => {
  const id = index + 1;
  addressDetails[`previous_address_${id}_street_name`] = Yup.string().when(
    ['step', 'addresses_num'],
    {
      is: (step, addresses_num) => step === '3' && addresses_num >= id,
      then: Yup.string()
        .required('Street Name is required')
        .matches(
          /^[a-z0-9\s\-./\\]+$/i,
          'Street Name must not contain special characters'
        ),
    }
  );
  addressDetails[`previous_address_${id}_town`] = Yup.string().when(
    ['step', 'addresses_num'],
    {
      is: (step, addresses_num) => step === '3' && addresses_num >= id,
      then: Yup.string()
        .required('Town is required')
        .matches(
          /^[a-z0-9\s\-./\\]+$/i,
          'Town must not contain special characters'
        ),
    }
  );
  addressDetails[`previous_address_${id}_postcode`] = Yup.string()
    .when(['step', 'addresses_num'], {
      is: (step, addresses_num) => step === '3' && addresses_num >= id,
      then: Yup.string()
        .required('Postcode is required')
        .matches(
          /^[a-z0-9\s\-./\\]+$/i,
          'Postcode must not contain special characters'
        ),
    })
    .max(10, 'Postcode must not exceed 10 characters');

  addressDetails[`previous_address_${id}_country`] = Yup.string().when(
    ['step', 'addresses_num'],
    {
      is: (value, addresses_num) => value === '3' && addresses_num >= id,
      then: Yup.string()
        .oneOf(
          ['GBR01', 'GBR02', 'GBR03'],
          'You must be a resident of England, Wales or Scotland'
        )
        .required(`Country is required`),
    }
  );
  addressDetails[`previous_address_${id}_years_lived`] = Yup.string().when(
    ['step', 'addresses_num'],
    {
      is: (value, addresses_num) => value === '3' && addresses_num >= id,
      then: Yup.string()
        .required(`Years lived is required`)
        .matches(/^[0-9]{1,2}$/, {
          message: 'Years lived must be a number and less than your age',
        }),
    }
  );
  addressDetails[`previous_address_${id}_months_lived`] = Yup.string().when(
    ['step', 'addresses_num'],
    {
      is: (value, addresses_num) => value === '3' && addresses_num >= id,
      then: Yup.string()
        .required(`Months lived is required`)
        .matches(/^[0-9]{1,2}$/, {
          message: 'Only numbers are allowed',
        })
        .test(
          'months-number',
          'Months lived must be between 0 and 12',
          (value) => {
            return Number(value) >= 0 && Number(value) <= 12;
          }
        ),
    }
  );
});

export const landingForm = Yup.object().shape({
  application: Yup.string().required('Application is required'),
  licenceNumber: Yup.string().when(['application'], {
    is: (value) => !['/provisional', '/exchange'].includes(value),
    then: Yup.string()
      .required(`Licence Number is required`)
      .min(16, 'Licence Number must be at least 16 characters')
      .max(16, 'Licence Number must not exceed 16 characters'),
  }),
  nin: Yup.string().matches(
    /^[A-CEGHJ-PR-TW-Z]{1}[A-CEGHJ-NPR-TW-Z]{1}[0-9]{6}[A-D]{0,1}$/i,
    {
      message: 'Please provide a valid National Insurance Number',
    }
  ),
  postcode: Yup.string()
    .required(`Postcode is required`)
    .matches(
      /^[a-z0-9\s\-./\\]+$/i,
      'Postcode must not contain special characters'
    )
    .max(10, 'Postcode must not exceed 10 characters'),
});
